import { Search, UserPlus, Users } from "lucide-react";
import { Panel, EmptyState, StatusBadge, formatDateTime } from "@/components/admin/ui";
import OrgUserActionsMenu from "./OrgUserActionsMenu";

const ROLE_BADGE_LABELS = { org_owner: "Owner", org_admin: "Org Admin", billing_admin: "Billing Admin", team_lead: "Team Lead", user: "Team Member" };

const FILTERS = [
  { key: "all", label: "All Users" },
  { key: "owners", label: "Owners" },
  { key: "org_admins", label: "Org Admins" },
  { key: "billing", label: "Billing" },
];

export default function OrgUsersList({ rows, counts, filter, onFilterChange, search, onSearchChange, onAction, onInvite, busyId }) {
  return (
    <Panel
      title="Organization Users"
      subtitle="Manage all users, their roles and access. Invite new users up to the limits above."
      testId="org-users-panel"
      action={
        <button
          type="button"
          onClick={onInvite}
          data-testid="org-users-invite-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <UserPlus size={15} /> Invite User
        </button>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="relative block w-full max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="org-users-search-input"
            placeholder="Search users by name or email…"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2" data-testid="org-users-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFilterChange(f.key)}
              data-testid={`org-users-filter-${f.key}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors duration-150 ${
                filter === f.key
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
              }`}
            >
              {f.label} ({counts[f.key] ?? 0})
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {rows.length === 0 ? (
          <EmptyState icon={Users} title="No users match this view." message="Try a different search or filter, or invite a new user." testId="org-users-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="org-users-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.1em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                  <th className="py-3 pr-4 font-bold">Name</th>
                  <th className="py-3 pr-4 font-bold">Email</th>
                  <th className="py-3 pr-4 font-bold">Role(s)</th>
                  <th className="py-3 pr-4 font-bold">Status</th>
                  <th className="py-3 pr-4 font-bold">Last Active</th>
                  <th className="py-3 pr-2 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`org-user-row-${row.id}`}>
                    <td className="py-3.5 pr-4">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-xs font-bold text-white">
                          {(row.name || row.email || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="font-bold text-slate-950 dark:text-white" data-testid={`org-user-name-${row.id}`}>{row.name || "—"}</span>
                      </div>
                    </td>
                    <td className="py-3.5 pr-4 text-slate-600 dark:text-slate-400" data-testid={`org-user-email-${row.id}`}>{row.email}</td>
                    <td className="py-3.5 pr-4">
                      <div className="flex flex-wrap gap-1.5" data-testid={`org-user-badges-${row.id}`}>
                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                          {ROLE_BADGE_LABELS[row.role] || row.role}
                        </span>
                        {row.kind === "user" && row.novaBilling && (
                          <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700 dark:bg-violet-500/10 dark:text-violet-400">Nova Billing</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 pr-4"><StatusBadge status={row.status} testId={`org-user-status-${row.id}`} /></td>
                    <td className="py-3.5 pr-4 text-slate-600 dark:text-slate-400" data-testid={`org-user-last-active-${row.id}`}>{row.lastActive ? formatDateTime(row.lastActive) : "—"}</td>
                    <td className="py-3.5 pr-2 text-right">
                      <OrgUserActionsMenu row={row} onAction={onAction} disabled={busyId === row.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
